import { ReactNode, useCallback } from 'react';
import { MBText } from '@shared/components/MBText';
import { View } from '@shared/components/View';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { formatRelativeTime } from '@shared/util/date';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { MBBaseChatBanner } from './base';

interface MBChannelChatBannerProps {
  channel: API.ChannelChatThreadFragment;
  creator: API.ShortUserFragment;
}

export function MBChannelChatBanner({ channel, creator }: MBChannelChatBannerProps) {
  const { navigate } = useNavigator();

  const addPeople = useCallback(() => {
    navigate('ChannelAddPeople', { channelSlug: channel.slug });
  }, [navigate, channel.slug]);

  const editChannel = useCallback(() => {
    navigate('ChannelEdit', { channelSlug: channel.slug });
  }, [navigate, channel.slug]);

  const editChannelName = useCallback(() => {
    navigate('ChannelEdit', { channelSlug: channel.slug, autoFocus: 'name' });
  }, [navigate, channel.slug]);

  const editChannelDescription = useCallback(() => {
    navigate('ChannelEdit', { channelSlug: channel.slug, autoFocus: 'description' });
  }, [navigate, channel.slug]);

  return (
    <MBBaseChatBanner channel={channel} subtitle={channel.description}>
      <View>
        <MBText>{i18n('chat.banner.doFromHere')}</MBText>
        <ChannelDetailsLink onPress={addPeople}>
          {i18n('chat.banner.addTeamMembers')}
        </ChannelDetailsLink>
        <ChannelDetailsLink onPress={editChannelName}>
          {i18n('chat.banner.renameWorkspace')}
        </ChannelDetailsLink>
        <ChannelDetailsLink onPress={editChannelDescription}>
          {i18n('chat.banner.addDescription')}
        </ChannelDetailsLink>
        <ChannelDetailsLink onPress={editChannel}>
          {i18n('chat.banner.addImage')}
        </ChannelDetailsLink>
      </View>

      <View paddingTop={2}>
        <MBText size="small" color={Colors.v2.greenSecondary90}>
          {i18n('chat.banner.createdBy', {
            name: creator.fullName,
            date: formatRelativeTime(channel.createdAt),
          })}
        </MBText>
      </View>
    </MBBaseChatBanner>
  );
}

function ChannelDetailsLink({ children, onPress }: { children: ReactNode; onPress: VoidFunction }) {
  return (
    <View row paddingTop={1}>
      <MBText>{'• '}</MBText>
      <MBText bold onPress={onPress}>
        {children}
      </MBText>
    </View>
  );
}
